import { AppDispatch, AppState } from 'app/store';
import axios from 'axios';
import { getAPI, postAPI } from 'services/api/apiAsync';
import api from 'services/api/apiInstance';
import {
  setCriticalNotificationsData,
  setCriticalNotificationsLoadStatus,
} from 'services/misc/criticalNotificationSlice';
import {
  selectToken,
  selectUserSessionId,
} from 'services/session/sessionSelectors';
import { selectUserId } from 'services/user/userSelectors';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';
import {
  API_ENDPOINTS,
  smartyAddressUrl,
  smartyEmbeddedKey,
} from 'utils/constants';
import { capitalize, getBrowser, getDevice, getOS } from 'utils';
import { LooseObjectType, SelectOptionType } from 'utils/globalTypes';
import { LegalPolicyType } from 'services/misc/types';

export function scanPassword(password: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const encodedPassword = encodeURIComponent(password);
      const config = {
        url: `${API_ENDPOINTS.passwordScan}/${encodedPassword}`,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      return response;
    } catch (error) {
      // return { noOfTimesAppeared: 2342 };
      return Promise.reject(error);
    }
  };
}

/**
 * Function to fetch the list of US states from the back-end service
 */
export async function fetchUSStates(): Promise<SelectOptionType[]> {
  try {
    const response = await api({
      url: API_ENDPOINTS.statesOfUS,
      method: 'get',
    });

    return response.data.map((state: LooseObjectType) => ({
      value: state.code,
      label: `${state.code} - ${state.name}`,
    }));
  } catch (error) {
    return [];
  }
}

// function to fetch notifications
export function fetchNotifications() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const token = selectToken(getState());
      const config = {
        url: API_ENDPOINTS.notifications,
        headers: {
          'X-XSRF-TOKEN': token,
        },
      };
      const response = await dispatch(getAPI(config)).unwrap();
      dispatch(setCriticalNotificationsData(response));
      dispatch(setCriticalNotificationsLoadStatus(true));
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// function to verify participation token
export function getEnrollmentInfo(token: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.enrollmentInfo}${token}`,
      };

      const response = await dispatch(getAPI(config)).unwrap();

      return !response.status;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// function to fetch data from cms
export function fetchDataFromCMS(filePath: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.termsOfService}${filePath}`,
        responseType: 'text' as const,
        headers: {
          Accept: 'text/html',
        },
      };
      const response = await dispatch(getAPI(config)).unwrap();

      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Fetches one brand-scoped legal document. The companion to `fetchDataFromCMS` above:
 * CMS-authored brands read a static `.md` file, the default brands read this endpoint.
 *
 * Returns `response.content` (the document HTML) rather than the whole `Policy` payload —
 * `version`, `type` and `enabledAt` come back too but nothing renders them.
 *
 * Brand scoping needs no work here: `makeAPIRequest` stamps `X-Idg-Brand` on every call.
 * Auth mirrors Angular's TokenInterceptor — the session token when there is a session,
 * nothing when signed out, since the legal pages must render for visitors who are not
 * signed in. The endpoint is deliberately in neither bearer-token set for that reason.
 */
export function fetchLegalPolicy(type: LegalPolicyType) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const token = selectToken(getState());
      const config = {
        url: `${API_ENDPOINTS.legalPolicies}${type}`,
        ...(token ? { headers: { 'X-XSRF-TOKEN': token } } : {}),
      };
      const response = await dispatch(getAPI(config)).unwrap();

      return response?.content ?? '';
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// Smarty address autocomplete
export function searchSmartyAddress(searchParams: LooseObjectType) {
  return async () => {
    try {
      const params = new URLSearchParams({
        key: smartyEmbeddedKey,
        ...searchParams,
      });
      const config = {
        url: `${smartyAddressUrl}?${params.toString()}`,
      };
      const response = await axios.get(config.url);
      return response?.data?.suggestions;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function userEventTracking(eventData: LooseObjectType) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const state = getState();
      const userId = selectUserId(state);
      const sessionId = selectUserSessionId(state);
      const currentPlanCode = selectOfferPlanCode(state);

      const config = {
        url: API_ENDPOINTS.userEventTracking,
        data: {
          userId,
          sessionId,
          currentPlanCode,
          pageUrl: window.location.href,
          browserType: getBrowser(),
          deviceType: capitalize(getDevice() ?? 'Desktop'),
          operatingSystem: capitalize(getOS()),
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          ...(eventData ?? {}),
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
